import { forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Tooltip from "../../tooltip/Tooltip";
import "./_date-field.scss";

/**
 * Pure-UI date input: a TextInput-styled trigger backed by react-datepicker's
 * popup calendar (see _components/filtering/FilterItem/DatePickerDropdown
 * for the sibling inline-calendar pattern this reuses the class-override
 * recipe from). `value` accepts a Date, an ISO string, or null/undefined;
 * `onChange` always reports a Date object - matches what the old
 * FormInputDateField's onChange gave callers, so existing formData readers
 * (e.g. buildValidationSchema's `new Date(value)` coercion) keep working.
 *
 * `showTime` (default false, backward-compatible): adds a time-of-day
 * column to the popup calendar and switches the trigger's default
 * placeholder to a date+time hint - covers what used to need a second,
 * separate component (`components/FormInput/DateTimeInputField`). Added
 * for the Object Customization migration's "Date and time" field type
 * rather than duplicating a near-identical date-only component - same
 * "extend before duplicating" call as Select's `isMulti`.
 */
const DateField = forwardRef(function DateField(
  {
    id,
    label,
    value,
    onChange,
    onBlur,
    error,
    disabled = false,
    placeholder,
    noPastDates = false,
    noFutureDates = false,
    showTime = false,
    tooltip,
    className = "",
  },
  ref
) {
  const selected = value ? new Date(value) : null;
  const resolvedPlaceholder = placeholder || (showTime ? "Select date & time" : "Select date");

  return (
    <Tooltip content={tooltip} disabled={!tooltip} className="date-field-tooltip-anchor">
      <div
        className={`date-field ${error ? "date-field--error" : ""} ${
          disabled ? "date-field--disabled" : ""
        } ${className}`}
      >
        {label && (
          <label className="date-field__label" htmlFor={id}>
            {label}
          </label>
        )}
        <div className="date-field__field-wrapper">
          <DatePicker
            ref={ref}
            id={id}
            selected={selected}
            onChange={(date) => onChange?.(date)}
            onBlur={onBlur}
            disabled={disabled}
            placeholderText={resolvedPlaceholder}
            minDate={noPastDates ? new Date() : undefined}
            maxDate={noFutureDates ? new Date() : undefined}
            // Without this, react-datepicker's own Navigation component
            // renders NO previous-month button at all (not just a disabled
            // one) once every day in the visible month is before minDate -
            // routine with noPastDates on the month containing "today" -
            // which read as a missing/broken chevron rather than a
            // legitimately unclickable one.
            showDisabledMonthNavigation
            showTimeSelect={showTime}
            timeIntervals={15}
            dateFormat={showTime ? "MMM d, yyyy h:mm aa" : "MMM d, yyyy"}
            className="date-field__input"
            calendarClassName="date-field__calendar"
            // Scopes the portaled popper wrapper (a sibling of
            // date-field__calendar, not a descendant - see this file's own
            // _date-field.scss) so its default pointer triangle can be
            // hidden without touching react-datepicker's popper elsewhere.
            popperClassName="date-field__popper"
            popperPlacement="bottom-start"
            autoComplete="off"
          />
        </div>
        {error && <p className="date-field__error">{error}</p>}
      </div>
    </Tooltip>
  );
});

export default DateField;
